import { type Consumer, createConsumer } from '@rails/actioncable';

import { ANYCABLE_URL } from 'ev-config/config';

import { fetchAnycableToken } from './auth';
import { clearAllSubscriptions } from './subscriptionRegistry';

let cachedConsumer: Consumer | null = null;
let cachedToken: string | null = null;
let inflightPromise: Promise<Consumer | null> | null = null;

/**
 * Create or return cached Action Cable consumer with JWT auth (jid query param).
 * Reuses one connection per token; clears cache when token changes.
 * Concurrent callers share the same in-flight request to avoid duplicate connections.
 *
 * Known limitation (V1): The JWT is fetched once and cached for the page session.
 * If the user keeps the app open long enough for the token to expire, Action Cable's
 * automatic reconnection will retry with the stale JWT and fail. Pusher has similar
 * session-lifetime auth behavior but handles re-auth internally during reconnect.
 * A future improvement should detect disconnection due to token expiry
 * and re-fetch the token before reconnecting.
 */
export async function getAnycableConsumer(
  connectionChannelName: string,
  authToken: string | undefined,
): Promise<Consumer | null> {
  if (cachedConsumer && cachedToken) {
    return cachedConsumer;
  }

  if (inflightPromise) {
    return inflightPromise;
  }

  inflightPromise = resolveConsumer(connectionChannelName, authToken).finally(
    () => {
      inflightPromise = null;
    },
  );

  return inflightPromise;
}

async function resolveConsumer(
  connectionChannelName: string,
  authToken: string | undefined,
): Promise<Consumer | null> {
  const token = await fetchAnycableToken(connectionChannelName, authToken);
  if (!token) {
    return null;
  }

  if (cachedConsumer && cachedToken === token) {
    return cachedConsumer;
  }

  if (cachedConsumer) {
    cachedConsumer.disconnect();
  }

  const url = `${ANYCABLE_URL}?jid=${encodeURIComponent(token)}`;
  cachedConsumer = createConsumer(url);
  cachedToken = token;
  return cachedConsumer;
}

export function disconnectAnycable(): void {
  clearAllSubscriptions();
  if (cachedConsumer) {
    cachedConsumer.disconnect();
    cachedConsumer = null;
    cachedToken = null;
  }
}
